import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    TextField,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBrain, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import {
    documentsApi,
    DocumentClauseResult,
    DocumentExtractionItem,
    DocumentListItem,
    DocumentStatus,
} from '../api/documents';
import { getApiErrorMessage, humanizeError } from '../lib/documentValidation';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

function pickCurrentAnalyticalFull(extractions: DocumentExtractionItem[] | undefined): DocumentExtractionItem | null {
    const list = extractions ?? [];
    const current = list.filter((e) => e.extraction_type === 'analytical_full' && e.is_current);
    if (current.length === 0) return null;
    return current.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];
}

export interface DocumentsAnalyticalPanelProps {
    doc: DocumentListItem;
    analyticalEnabled: boolean;
    normalizedStatus: DocumentStatus;
    onToast: (message: string, severity: 'success' | 'error' | 'info') => void;
    onRunDeepAnalysis: (doc: DocumentListItem) => Promise<void>;
    onRefreshAfterAsk: () => void;
}

const DocumentsAnalyticalPanel: React.FC<DocumentsAnalyticalPanelProps> = ({
    doc,
    analyticalEnabled,
    normalizedStatus,
    onToast,
    onRunDeepAnalysis,
    onRefreshAfterAsk,
}) => {
    const [open, setOpen] = useState(false);
    const [loadState, setLoadState] = useState<LoadState>('idle');
    const [loadError, setLoadError] = useState<string | null>(null);
    const [clauses, setClauses] = useState<DocumentClauseResult | null>(null);
    const [extractions, setExtractions] = useState<DocumentExtractionItem[]>([]);
    const [runningDeep, setRunningDeep] = useState(false);
    const [question, setQuestion] = useState('');
    const [asking, setAsking] = useState(false);
    const [answer, setAnswer] = useState<string | null>(null);

    const analyticalFull = useMemo(() => pickCurrentAnalyticalFull(extractions), [extractions]);

    const hasStoredDeepResults = Boolean(clauses?.clauses?.length) || Boolean(analyticalFull);

    const fetchPanelData = useCallback(async () => {
        setLoadState('loading');
        setLoadError(null);

        const [clauseRes, extractionRes] = await Promise.allSettled([
            documentsApi.getDocumentClauses(doc.id),
            documentsApi.getDocumentExtractions(doc.id),
        ]);

        if (clauseRes.status === 'fulfilled') {
            setClauses(clauseRes.value);
        } else {
            setClauses(null);
        }

        if (extractionRes.status === 'fulfilled') {
            setExtractions(Array.isArray(extractionRes.value) ? extractionRes.value : []);
        } else {
            setExtractions([]);
        }

        if (clauseRes.status === 'rejected' && extractionRes.status === 'rejected') {
            setLoadState('error');
            setLoadError(
                humanizeError(getApiErrorMessage(clauseRes.reason || extractionRes.reason)),
            );
            return;
        }

        setLoadState('ready');
    }, [doc.id]);

    useEffect(() => {
        if (!open || loadState !== 'idle') return;
        void fetchPanelData();
    }, [open, loadState, fetchPanelData]);

    const handleToggle = () => {
        setOpen((v) => !v);
        if (!open && loadState === 'idle') {
            // effect will load
        }
    };

    const handleAsk = async () => {
        const q = question.trim();
        if (!q) return;
        setAsking(true);
        setAnswer(null);
        try {
            const res = await documentsApi.askDocument(doc.id, q);
            setAnswer(res.answer);
            onToast('Answer ready below.', 'success');
            onRefreshAfterAsk();
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === 'object' && detail.code === 'ANALYTICAL_NOT_AVAILABLE') {
                onToast(
                    typeof detail.message === 'string'
                        ? detail.message
                        : 'Document Q&A is not included with your account.',
                    'info',
                );
            } else {
                onToast(humanizeError(getApiErrorMessage(e)), 'error');
            }
        } finally {
            setAsking(false);
        }
    };

    const handleRunDeep = async () => {
        setRunningDeep(true);
        try {
            await onRunDeepAnalysis(doc);
            if (open) void fetchPanelData();
        } finally {
            setRunningDeep(false);
        }
    };

    const longSummaryText =
        analyticalFull?.result_json && typeof analyticalFull.result_json.long_summary === 'string'
            ? analyticalFull.result_json.long_summary
            : null;

    const showNotIncludedCopy =
        !analyticalEnabled && !hasStoredDeepResults && doc.mode !== 'analytical';
    const showUnavailableAnalyticalModeCopy =
        !analyticalEnabled && !hasStoredDeepResults && doc.mode === 'analytical';

    return (
        <Box sx={{ mt: 1.5, borderTop: '1px solid var(--color-border)', pt: 1.5 }}>
            <Button
                size="small"
                onClick={handleToggle}
                startIcon={<FontAwesomeIcon icon={faBrain} style={{ fontSize: '0.8rem' }} />}
                endIcon={
                    <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} style={{ fontSize: '0.65rem' }} />
                }
                sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    color: 'var(--color-text-secondary)',
                    px: 0.5,
                    minWidth: 0,
                }}
            >
                Deep analysis
            </Button>
            <Collapse in={open}>
                <Box sx={{ mt: 1, pl: 0.5, pr: 0.5 }}>
                    {loadState === 'loading' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                            <CircularProgress size={18} sx={{ color: 'var(--color-text-muted)' }} />
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                Loading saved analysis…
                            </Typography>
                        </Box>
                    )}
                    {loadState === 'error' && loadError && (
                        <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', display: 'block' }}>
                            {loadError}
                        </Typography>
                    )}
                    {loadState === 'ready' && (
                        <>
                            {showNotIncludedCopy && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'var(--color-text-secondary)', display: 'block', mb: 1.5 }}
                                >
                                    Deep analysis is not included with your account. This is not available as a separate
                                    purchase in billing today—we will show saved results here when they already exist
                                    from earlier access.
                                </Typography>
                            )}

                            {showUnavailableAnalyticalModeCopy && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'var(--color-text-secondary)', display: 'block', mb: 1.5 }}
                                >
                                    This document was previously opened for deep analysis, but your account does not
                                    include new deep-analysis runs right now. Saved clause or summary outputs will
                                    still appear here whenever they exist.
                                </Typography>
                            )}

                            {analyticalEnabled &&
                                doc.mode !== 'analytical' &&
                                (normalizedStatus === 'processed' || normalizedStatus === 'partial') && (
                                <Box sx={{ mb: 1.5 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={runningDeep}
                                        onClick={() => void handleRunDeep()}
                                        sx={{ textTransform: 'none', fontSize: '0.72rem' }}
                                    >
                                        {runningDeep ? 'Starting…' : 'Run deep analysis on this document'}
                                    </Button>
                                </Box>
                            )}

                            {analyticalEnabled && doc.mode === 'analytical' && normalizedStatus === 'processing' && (
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 1 }}>
                                    Processing is still running. Deep analysis outputs will appear below when pages finish and
                                    the deep pass completes.
                                </Typography>
                            )}

                            {analyticalEnabled && doc.mode === 'analytical' && (
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 1 }}>
                                    Deep analysis is enabled for this document. Saved clause and summary outputs appear
                                    below when ready.
                                </Typography>
                            )}

                            {!analyticalEnabled && hasStoredDeepResults && (
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 1 }}>
                                    Your account no longer includes new deep analysis, but earlier results stay visible
                                    here. Q&A stays off until access returns.
                                </Typography>
                            )}

                            {longSummaryText && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Analytical summary
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}
                                    >
                                        {longSummaryText}
                                    </Typography>
                                </Box>
                            )}

                            {clauses?.clauses && clauses.clauses.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Clause breakdown
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {clauses.clauses.slice(0, 12).map((c) => (
                                            <Box
                                                key={c.clause_index}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor: 'rgba(148, 163, 184, 0.08)',
                                                    border: '1px solid rgba(148, 163, 184, 0.2)',
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                                    {c.title}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                                                    {c.text}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {loadState === 'ready' &&
                                !hasStoredDeepResults &&
                                doc.mode === 'analytical' &&
                                analyticalEnabled && (
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                        No clause or long-form analytical output is stored yet. If analysis just
                                        finished, try collapsing and opening again—or run processing again from the row
                                        actions if something was interrupted.
                                    </Typography>
                                )}

                            {analyticalEnabled && doc.mode === 'analytical' && normalizedStatus === 'processed' && (
                                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed rgba(148, 163, 184, 0.35)' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Ask about this document
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Ask a question about the document text…"
                                        value={question}
                                        onChange={(e) => setQuestion(e.target.value)}
                                        sx={{ mb: 1 }}
                                    />
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={asking || !question.trim()}
                                        onClick={() => void handleAsk()}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                    >
                                        {asking ? 'Asking…' : 'Get answer'}
                                    </Button>
                                    {answer && (
                                        <Typography
                                            variant="body2"
                                            sx={{ mt: 1.5, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}
                                        >
                                            {answer}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

export default DocumentsAnalyticalPanel;
